<script setup>
import { ref, reactive, computed, watch } from 'vue';
import { useAlert } from 'dashboard/composables';
// Next UI Components
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import PhoneNumberInput from 'dashboard/components-next/phonenumberinput/PhoneNumberInput.vue';
import Input from 'dashboard/components-next/input/Input.vue';
// Data
import { tanzaniaLocations } from 'dashboard/constants/tanzania_locations';

const props = defineProps({
  message: { type: Object, required: true },
  conversation: { type: Object, default: () => ({}) }
});

const emit = defineEmits(['close']);

const isSubmitting = ref(false);

const state = reactive({
  title: '',
  description: props.message.content || '',
  feedbackType: 'COMPLAINT', 
  region: '',
  district: '',
  complainantName: props.conversation?.meta?.sender?.name || '',
  phoneNo: props.conversation?.meta?.sender?.phone_number || '',
});

// Options for the Feedback Type
const feedbackTypeOptions = [
  { label: 'Complaint', value: 'COMPLAINT' },
  { label: 'Suggestion', value: 'SUGGESTION' },
  { label: 'Inquiry', value: 'INQUIRY' },
  { label: 'Compliment', value: 'COMPLIMENT' },
  { label: 'Information', value: 'INFORMATION' },
];
// --- Cascading Logic ---

const regionOptions = computed(() => 
  tanzaniaLocations.map(r => ({ label: r.name, value: r.name }))
);

const districtOptions = computed(() => {
  const selectedRegionData = tanzaniaLocations.find(r => r.name === state.region);
  if (!selectedRegionData) return [];
  return selectedRegionData.districts.map(d => ({ label: d.name, value: d.name }));
});

// Reset district if region changes
watch(() => state.region, () => {
  state.district = '';
});

// --- Form Submission ---

const handleSubmit = async () => {
  const pathParts = window.location.pathname.split('/');
  const accountIndex = pathParts.indexOf('accounts');
  const accountId = accountIndex !== -1 ? pathParts[accountIndex + 1] : null;

  if (!accountId) return;

  isSubmitting.value = true;
  try {
    const response = await window.axios.post(`/api/v1/accounts/${accountId}/emrejesho`, {
      ...state,
      message_id: props.message.id
    });
    
    const trackingNo = response.data.data.data.trackNo; 
    
    if (window.bus) {
      window.bus.$emit('new_notification', `Registered! Tracking No: ${trackingNo}`);
    }

    await window.axios.post(`/api/v1/accounts/${accountId}/conversations/${props.message.conversation_id}/messages`, {
      content: `Sent to eMrejesho. Tracking ID: ${trackingNo}`,
      message_type: 'outgoing',
      private: true 
    });

    emit('close');
  } catch (error) {
    console.error("Submission failed:", error);
    alert('Failed to send complaint.');
  } finally {
    isSubmitting.value = false;
  }
};
</script>

<template>
  <div class="flex items-center justify-center w-full h-full">
    <div class="w-full divide-y divide-n-strong flex flex-col bg-n-background border border-n-strong shadow-xl rounded-xl overflow-hidden">
      <div class="p-4 flex justify-between items-center bg-n-alpha-1">
        <h3 class="text-lg font-medium text-n-slate-12 text-center w-full">Send to eMrejesho</h3>
      </div>

      <div class="p-6 space-y-5 max-h-[70vh] overflow-y-auto">
        <div class="flex flex-col gap-2">
            <label class="text-xs font-semibold uppercase text-n-slate-11">Feedback Type</label>
            <ComboBox
                v-model="state.feedbackType"
                :options="feedbackTypeOptions"
                placeholder="Select Type"
            />
        </div>
        
        <div class="flex flex-col gap-2">
          <label class="text-xs font-semibold uppercase text-n-slate-11">Subject</label>
          <Input 
            v-model="state.title" 
            placeholder="Brief title of the complaint" 
            custom-input-class="h-10"
          />
        </div>

        <div class="flex flex-col gap-2">
          <label class="text-xs font-semibold uppercase text-n-slate-11">Description</label>
          <textarea 
            v-model="state.description" 
            rows="4" 
            class="w-full bg-n-alpha-2 border border-n-strong rounded-lg px-3 py-2 text-n-slate-12 outline-none focus:border-n-brand transition-colors resize-none"
          />
        </div>

        <div class="grid grid-cols-2 gap-4 pt-2">
          <div class="flex flex-col gap-2">
            <label class="text-xs font-semibold uppercase text-n-slate-11">Region</label>
            <ComboBox
              v-model="state.region"
              :options="regionOptions"
              placeholder="Select Region"
            />
          </div>
          <div class="flex flex-col gap-2">
            <label class="text-xs font-semibold uppercase text-n-slate-11">District</label>
            <ComboBox
              v-model="state.district"
              :options="districtOptions"
              :disabled="!state.region"
              placeholder="Select District"
            />
          </div>
        </div>

        <div class="grid grid-cols-2 gap-4 pt-2 border-t border-n-strong">
          <div class="flex flex-col gap-2">
            <label class="text-xs font-semibold uppercase text-n-slate-11">Complainant Name</label>
            <Input v-model="state.complainantName" placeholder="Name" custom-input-class="h-10" />
          </div>
          <div class="flex flex-col gap-2">
            <label class="text-xs font-semibold uppercase text-n-slate-11">Phone Number</label>
            <PhoneNumberInput
              v-model="state.phoneNo"
              placeholder="Enter phone number"
            />
          </div>
        </div>
      </div>

      <div class="p-4 flex justify-end gap-3 bg-n-alpha-1">
        <button class="px-4 py-2 text-n-slate-11 font-medium hover:text-n-slate-12" @click="emit('close')">
          Cancel
        </button>
        <button 
          :disabled="isSubmitting || !state.title || !state.region"
          class="px-6 py-2 bg-blue-600 text-white rounded-lg font-medium shadow-sm hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-2"
          @click="handleSubmit"
        >
          <span v-if="isSubmitting" class="animate-spin border-2 border-white border-t-transparent rounded-full w-4 h-4"></span>
          {{ isSubmitting ? 'Registering...' : 'Send Complaint' }}
        </button>
      </div>
    </div>
  </div>
</template>